import { CircularProgress, IconButton, makeStyles } from '@material-ui/core';
import { CameraAlt, Fullscreen, FullscreenExit } from '@material-ui/icons';
import clsx from 'clsx';
import { memo, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageGallery, { ReactImageGalleryItem } from 'react-image-gallery';
import useAuthenticatedImageSrc, { isUcpImageUrl } from 'src/hooks/useAuthenticatedImageSrc';
import { useCustomerMode } from 'src/hooks/useCustomerMode';
import useValidatedImageLinks from 'src/hooks/useValidatedImageLinks';

import { IndividualBidDTO, IndividualBidStateInfoDTO } from '../modules/generated/api';

import GravelBidInfo from './GravelBidInfo';
import ImageLabel from './ImageLabel';
import ImageWithAuthentication from './ui/ImageWithAuthentication';
import WatchlistIcon from './WatchlistIcon';

const FALLBACK_IMAGE_SRC = '/images/CameraAlt.png';

const handleImageError = (e: SyntheticEvent<HTMLImageElement>) => {
  (e.target as HTMLImageElement).src = FALLBACK_IMAGE_SRC;
};

const useStyles = makeStyles(({ spacing, palette: { galleryIcon, common, grey } }) => ({
  root: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    '&--big': {
      aspectRatio: '4/3',
      minHeight: spacing(37),
      marginBottom: spacing(2),
      display: 'flex',
      maxWidth: '100%',
      '@media print': {
        fontSize: 'xxx-large',
      },
    },
    '&--small': {
      aspectRatio: '4/3',
      width: spacing(10),
      minHeight: spacing(7),
    },
    '&--medium': {
      aspectRatio: '4/3',
      width: spacing(18),
      display: 'block',
    },
    '& .fullscreen-modal': {
      zIndex: '1005 !important' as any,
    },
    '@media print': {
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
    },
  },
  showOnlyOnPrint: {
    display: 'none',
    '@media print': {
      maxWidth: '35vw',
      display: 'block',
    },
  },
  loadingWrapper: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: {
    display: 'block',
    width: '100%',
    backgroundColor: grey[200],
  },
  iconBig: {
    fontSize: '8rem',
  },
  photoGalleryImage: {
    height: '70vh',
    overflow: 'hidden',
  },
  preview: {
    aspectRatio: '4/3',
    height: 'auto',
  },
  noOpacity: {
    opacity: 0,
  },
  imageBackground: {
    display: 'block',
    backgroundColor: grey[200],
    height: '100%',
  },
  imageGallery: {
    '& .image-gallery-content': {
      backgroundColor: grey[200],
      justifyContent: 'center',
      alignItems: 'center',
      flexWrap: 'wrap',
      '@media print': {
        display: 'none',
      },
    },
    '& .image-gallery-svg': {
      height: '70px',
      '&:hover': {},
      '@media print': {
        display: 'none',
      },
    },
    '& .image-gallery-icon': {
      filter: 'none',
      color: galleryIcon.main,
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          color: galleryIcon.dark,
        },
      },
      '@media print': {
        display: 'none',
      },
    },
    '& .image-gallery-fullscreen-button': {
      top: '0',
      height: 'fit-content',
      padding: '5px 10px',
      '& svg': {
        height: '40px',
      },
    },
    '& .image-gallery-index': {
      left: 0,
      width: 'fit-content',
    },
    '& .image-gallery-bullet': {
      marginBottom: spacing(0.4),
      boxShadow: 'none',
      background: galleryIcon.light,
      '&.active': {
        background: galleryIcon.main,
      },
      '@media print': {
        display: 'none',
      },
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          background: galleryIcon.main,
          borderColor: common.white,
        },
        ' &.active:hover': {
          background: galleryIcon.main,
        },
      },
    },
  },
  imageBox: {
    display: 'flex',
    justifyContent: 'flex-end',
    '@media print': {
      display: 'none',
    },
  },
  centerFullscreen: {
    paddingTop: '8vh',
    '& .image-gallery-image': {
      height: '80vh',
    },
  },
  fullscreenIcon: {
    position: 'absolute',
  },
  inlineBidInfo: {
    top: '0.75rem',
    right: '3rem',
  },
  singlePicBid: {
    top: '0.4rem',
    right: '0.4rem',
  },
}));

type CarsTableImageCellProps = {
  imageLinks?: string[];
  thumbnail?: string;
  size?: 'small' | 'medium' | 'big';
  bidStatus?: IndividualBidStateInfoDTO;
  isExpired?: boolean;
  bought?: boolean;
  otherBought?: IndividualBidDTO;
  vin?: string;
  watchlist?: string[];
  showWatchlistIcon?: boolean;
};

const CarsTableImageCell = ({
  imageLinks,
  thumbnail: thumbnailProp,
  size = 'small',
  bidStatus,
  isExpired = false,
  bought,
  otherBought,
  vin,
  watchlist,
  showWatchlistIcon = false,
}: CarsTableImageCellProps) => {
  const classes = useStyles();
  const { customerModeActive } = useCustomerMode();
  // Use thumbnail prop if provided and different from imageLinks[0]
  const thumbnail = thumbnailProp && imageLinks && thumbnailProp !== imageLinks[0] ? thumbnailProp : imageLinks?.[0];

  const processedImageLinks =
    thumbnailProp && imageLinks && thumbnailProp !== imageLinks[0] ? [thumbnailProp, ...imageLinks] : imageLinks;

  const effectiveThumbnail = useAuthenticatedImageSrc(thumbnail, isUcpImageUrl(thumbnail));
  const [loading, setLoading] = useState(thumbnail !== undefined);
  const [error, setError] = useState(false);
  const [fullScreenState, setFullscreenState] = useState(false);
  const fullscreenBtnRef = useRef<HTMLButtonElement>(null);
  const [index, setIndex] = useState(0);

  const galleryLinks = processedImageLinks && processedImageLinks.length > 1 ? processedImageLinks : undefined;
  const { validLinks, allBroken, validating } = useValidatedImageLinks(galleryLinks);
  const clampedIndex = Math.min(index, Math.max(0, validLinks.length - 1));

  const { t } = useTranslation();
  const expiredOrSoldLabel = bought || otherBought ? t('referencePopup.sold') : t('bids.expired');
  const timeout = useRef<number>();

  const handleOnLoad = () => {
    setError(false);

    // Flicker prevention
    timeout.current = window.setTimeout(() => {
      setLoading(false);
    }, 400);
  };

  const handleOnError = () => {
    setError(true);
    setLoading(false);
  };

  const renderGalleryItem = (item: ReactImageGalleryItem) => (
    <ImageWithAuthentication
      src={item.original}
      alt={item.originalAlt ?? 'car'}
      className={clsx('image-gallery-image', classes.imageBackground)}
      authenticationNeeded={isUcpImageUrl(item.original)}
      onLoad={handleOnLoad}
      onError={handleImageError}
    />
  );

  const renderGalleryThumbInner = (item: ReactImageGalleryItem) => (
    <span className="image-gallery-thumbnail-inner">
      <ImageWithAuthentication
        src={item.thumbnail}
        alt={item.thumbnailAlt ?? 'car'}
        className={clsx('image-gallery-thumbnail-image', classes.imageBackground)}
        authenticationNeeded={isUcpImageUrl(item.thumbnail)}
        onError={handleImageError}
      />
    </span>
  );

  const toggleFullScreen = () => {
    if (fullscreenBtnRef?.current?.click) fullscreenBtnRef.current.click();
  };

  useEffect(
    () => () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    },
    [],
  );

  return (
    <div className={clsx(`${classes.root}--${size}`, classes.root)}>
      {(loading || validating) && !allBroken && (
        <div className={classes.loadingWrapper}>
          <CircularProgress size={size === 'small' ? 16 : 64} />
        </div>
      )}

      {(error || !thumbnail || allBroken) && (
        <div className={classes.loadingWrapper}>
          {showWatchlistIcon && <WatchlistIcon vin={vin} watchlist={watchlist || []} />}
          <ImageLabel size="big" text={expiredOrSoldLabel} disable={!isExpired}>
            <CameraAlt fontSize="large" color="disabled" className={clsx(size === 'big' && classes.iconBig)} />
            {bidStatus && !customerModeActive && (
              <GravelBidInfo bidStatus={bidStatus} className={classes.singlePicBid} />
            )}
          </ImageLabel>
        </div>
      )}

      {processedImageLinks && processedImageLinks.length === 1 ? (
        <>
          {showWatchlistIcon && <WatchlistIcon vin={vin} watchlist={watchlist || []} />}
          <div className={classes.imageBox} key={vin}>
            <ImageLabel size="big" text={expiredOrSoldLabel} disable={!isExpired}>
              <img
                className={clsx(classes.image, loading && classes.noOpacity)}
                key={thumbnail}
                src={effectiveThumbnail}
                alt="car"
                onLoad={handleOnLoad}
                onError={handleOnError}
              />
              {bidStatus && !error && !customerModeActive && (
                <GravelBidInfo bidStatus={bidStatus} className={classes.singlePicBid} />
              )}
            </ImageLabel>
          </div>
          <img
            src={effectiveThumbnail}
            alt={`${t('common.car')} ${t('car.picture')}`}
            className={classes.showOnlyOnPrint}
          />
        </>
      ) : (
        validLinks.length > 1 &&
        !validating &&
        !error && (
          <ImageLabel size="big" text={expiredOrSoldLabel} disable={!isExpired}>
            <ImageGallery
              additionalClass={clsx(classes.imageGallery, fullScreenState && classes.centerFullscreen)}
              showBullets
              useBrowserFullscreen
              showPlayButton={false}
              showThumbnails={fullScreenState}
              showIndex={fullScreenState}
              onClick={toggleFullScreen}
              renderItem={renderGalleryItem}
              renderThumbInner={renderGalleryThumbInner}
              renderFullscreenButton={(onClick, isFullscreen) => (
                <>
                  <div>
                    {showWatchlistIcon && !isFullscreen && <WatchlistIcon vin={vin} watchlist={watchlist || []} />}
                    {bidStatus && !fullScreenState && !customerModeActive && (
                      <GravelBidInfo bidStatus={bidStatus} className={classes.inlineBidInfo} />
                    )}
                  </div>
                  <IconButton
                    ref={fullscreenBtnRef}
                    onClick={onClick}
                    className={clsx(
                      `image-gallery-fullscreen-button${isFullscreen ? ' active' : ''}`,
                      'image-gallery-icon',
                      classes.fullscreenIcon,
                    )}
                  >
                    {isFullscreen ? (
                      <FullscreenExit className="image-gallery-svg" />
                    ) : (
                      <Fullscreen className="image-gallery-svg" />
                    )}
                  </IconButton>
                </>
              )}
              stopPropagation
              onSlide={(currentIndex) => {
                setIndex(currentIndex);
              }}
              startIndex={clampedIndex}
              onScreenChange={() => setFullscreenState(!fullScreenState)}
              items={validLinks.map(
                (img) =>
                  ({
                    original: img,
                    originalClass: clsx(classes.photoGalleryImage, !fullScreenState && classes.preview),
                    thumbnail: img,
                  }) as ReactImageGalleryItem,
              )}
            />
            <ImageWithAuthentication
              src={validLinks[clampedIndex]}
              alt={`${t('common.car')} ${t('car.picture')}`}
              className={classes.showOnlyOnPrint}
              authenticationNeeded={isUcpImageUrl(validLinks[clampedIndex])}
            />
          </ImageLabel>
        )
      )}
    </div>
  );
};

export default memo(CarsTableImageCell);
